import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useEffect, useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { Notes } from "../../../../components/dashboard/notes/notes";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import {
  ArrowPathRoundedSquareIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  CodeBracketIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

import {
  customerHeaderAction,
  dispatchWithToast,
} from "../../../../components/Utils";
import { HistoriesModalButton } from "../../../../components/dashboard/histories/modal-button";

import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import ChecklistItemLines from "../../../../components/dashboard/quality/checklists/checklistTypeItemLines";
import {
  CHECKLIST_RESPONSE_ACTION_KEYS,
  normalizeActionKeys,
  toggleKeyInList,
} from "../../../../components/dashboard/quality/checklists/checklistResponseConfig";
import {
  createChecklistTypeAsync,
  updateChecklistTypeAsync,
} from "../../../../slices/quality/checklist-type";
import { getChecklistType } from "../../../../api/quality/checklist";
import {
  fetchChecklistTypePdf,
  registerChecklistTypeHse,
} from "../../../../api/quality/checklistType";
import { updateHseDocument } from "../../../../api/hse/documents";
import { DisciplineCategoryChapterSelector } from "../../../../components/dashboard/documents/DisciplineCategoryChapterSelector";
import { FormikSaveBar } from "../../../../components/utils/FormikSaveBar";
import { AIGenerator } from "../../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";
import { ChecklistTypeJsonDialog } from "../../../../components/dashboard/quality/checklists/ChecklistTypeJsonDialog";
import { parseImportedChecklistTypeJson } from "../../../../components/dashboard/quality/checklists/checklistTypeImportUtils";
import {
  buildChecklistTypeSavePayload,
  mergeImportedItemsWithExistingIds,
} from "../../../../components/dashboard/quality/checklists/checklistTypeSavePayload";

export const ChecklistTypeView = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");

  const { open, onClose } = props;

  const [itemState, setItemState] = useState({
    checklist_type: {},
    error: "",
    errors: [],
    isLoading: true,
  });

  const { checklist_type, isLoading, error, errors } = itemState;

  const hseRevisionStatus = checklist_type?.hse_revision_status || {};
  const hseRevisionPending = hseRevisionStatus.pending_revision === true;
  const hseDraftSnapshotStale = hseRevisionStatus.draft_snapshot_stale === true;
  const hseDraftInProgress = hseRevisionStatus.draft_revision_in_progress === true;
  const hseNeedsDocumentUpdate = hseRevisionPending || hseDraftSnapshotStale;
  const hseDocumentId = checklist_type?.hse_document?.id;

  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCreatingHseRevision, setIsCreatingHseRevision] = useState(false);
  const [isRegisteringHse, setIsRegisteringHse] = useState(false);
  const [hseDisciplineId, setHseDisciplineId] = useState("");
  const [hseCategoryId, setHseCategoryId] = useState("");
  const [hseChapterNumber, setHseChapterNumber] = useState("");

  useEffect(() => {
    const hse = checklist_type?.hse_document;
    if (hse?.id) {
      setHseDisciplineId(hse.discipline?.id ? String(hse.discipline.id) : "");
      setHseCategoryId(hse.category?.id ? String(hse.category.id) : "");
      setHseChapterNumber(hse.chapter?.number ? String(hse.chapter.number) : "");
      return;
    }
    if (checklist_type?.id) {
      setHseDisciplineId("");
      setHseCategoryId("");
      setHseChapterNumber("");
    }
  }, [checklist_type?.id, checklist_type?.hse_document]);

  const reloadChecklistType = useCallback(async () => {
    if (!id) return;
    const res = await getChecklistType(id);
    if (res?.status === 200) {
      setItemState((prevState) => ({
        ...prevState,
        checklist_type: res?.data?.data,
      }));
    }
  }, [id]);

  useEffect(() => {
    const fetchChecklistType = async (typeId) => {
      const res = await getChecklistType(typeId);
      if (res?.status === 200) {
        setItemState((prevState) => ({
          ...prevState,
          checklist_type: res?.data?.data,
          isLoading: false,
        }));
      }
    };

    if (id) {
      fetchChecklistType(id);
    } else {
      setItemState((prevState) => ({
        ...prevState,
        checklist_type: {},
        isLoading: false,
      }));
    }
  }, [id]);

  const formDirtyRef = useRef(false);

  useEffect(() => {
    const reloadIfPristine = () => {
      if (formDirtyRef.current) return;
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      reloadChecklistType();
    };

    window.addEventListener("focus", reloadIfPristine);
    document.addEventListener("visibilitychange", reloadIfPristine);
    return () => {
      window.removeEventListener("focus", reloadIfPristine);
      document.removeEventListener("visibilitychange", reloadIfPristine);
    };
  }, [reloadChecklistType]);

  const createOrUpdateDispatch = (data) => {
    const payload = buildChecklistTypeSavePayload(data, {
      sourceItems: checklist_type?.items,
    });
    if (checklist_type?.id) {
      const res = dispatch(updateChecklistTypeAsync(payload));
      return res;
    } else {
      const res = dispatch(createChecklistTypeAsync(payload));
      return res;
    }
  };

  const headerMeta = [
    customerHeaderAction(checklist_type?.customer),
    {
      label: checklist_type?.project?.displayTitle,
      description: "",
      path: "/dashboard/projects/" + checklist_type?.project?.id,
      action: null,
      icon: BuildingOfficeIcon,
      textColor: "text-teal-700",
      hidden: checklist_type?.project?.id ? false : true,
    },
  ];

  const handleCreateHseRevision = async () => {
    if (!hseDocumentId) return;
    setIsCreatingHseRevision(true);
    try {
      await updateHseDocument(
        hseDocumentId,
        {},
        {
          create_revision: true,
          change_summary: t("quality.checklist.type.hse.checklist_revision_summary"),
        }
      );
      toast.success(t("quality.checklist.type.hse.revision_created"));
      await reloadChecklistType();
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.[0] ||
        t("quality.checklist.type.hse.revision_create_failed");
      toast.error(errorMessage);
    } finally {
      setIsCreatingHseRevision(false);
    }
  };

  const registerInHms = async () => {
    if (!checklist_type?.id) {
      toast.error(t("quality.checklist.type.hse.save_type_first"));
      return;
    }
    if (!hseCategoryId) {
      toast.error(t("quality.checklist.type.hse.category_required"));
      return;
    }
    setIsRegisteringHse(true);
    const res = await registerChecklistTypeHse(checklist_type.id, {
      discipline_id: hseDisciplineId || undefined,
      category_id: hseCategoryId,
      chapter_number: hseChapterNumber || undefined,
    });
    setIsRegisteringHse(false);
    if (res?.errors) {
      toast.error(
        Array.isArray(res.errors)
          ? res.errors.join(", ")
          : t("quality.checklist.type.hse.register_failed")
      );
      return;
    }
    toast.success(res?.message || t("quality.checklist.type.hse.registered"));
    if (res?.type) {
      setItemState((prevState) => ({
        ...prevState,
        checklist_type: res.type,
      }));
    }
  };

  const headerActions = [
    {
      component: (
        <button
          type="button"
          disabled={isPrinting || !checklist_type?.id}
          onClick={async () => {
            setIsPrinting(true);
            const res = await fetchChecklistTypePdf(checklist_type.id);
            setIsPrinting(false);
            if (res?.pdf_url) {
              window.open(res.pdf_url, "_blank", "noopener");
            } else {
              toast.error(t("quality.checklist.type.print_failed"));
            }
          }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          <PrinterIcon className="h-4 w-4" />
          {isPrinting ? t("Loading") : t("quality.checklist.type.print")}
        </button>
      ),
      label: "Print",
      description: "",
      icon: PrinterIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: !checklist_type?.id,
      menu: false,
    },
    {
      component: (
        <HistoriesModalButton
          module={"checklist_type"}
          module_param={checklist_type?.id}
        />
      ),
      label: "History",
      description: "",
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
    },
  ];

  const renderHseAlertBanners = () => {
    if (isLoading || !checklist_type?.id || !hseDocumentId) {
      return null;
    }

    const banners = [];

    if (hseNeedsDocumentUpdate) {
      banners.push(
        <div
          key="hse-needs-update"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 sm:px-5"
        >
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {t("quality.checklist.type.hse.document_needs_update")}
              </p>
              <p className="mt-1 text-sm text-amber-800">
                {hseDraftInProgress
                  ? t("quality.checklist.type.hse.document_needs_update_open_hint")
                  : t("quality.checklist.type.hse.document_needs_update_create_hint")}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 sm:pl-8">
            {!hseDraftInProgress ? (
              <button
                type="button"
                onClick={handleCreateHseRevision}
                disabled={isCreatingHseRevision}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                {isCreatingHseRevision
                  ? t("Loading")
                  : t("quality.checklist.type.hse.create_revision")}
              </button>
            ) : null}
            <Link
              to={returnTo || `/dashboard/hse-ks/documents/${hseDocumentId}`}
              className="inline-flex items-center justify-center rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            >
              {t("quality.checklist.type.hse.open_document")}
            </Link>
          </div>
        </div>
      );
    }

    if (hseDraftInProgress) {
      banners.push(
        <div
          key="hse-draft-in-progress"
          className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 sm:px-5"
        >
          <div className="flex items-start gap-3">
            <DocumentArrowDownIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-blue-900">
                {t("quality.checklist.type.hse.revision_in_progress_title")}
              </p>
              <p className="mt-1 text-sm text-blue-800">
                {t("quality.checklist.type.hse.revision_in_progress_edit_hint")}
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 sm:pl-8">
            <Link
              to={returnTo || `/dashboard/hse-ks/documents/${hseDocumentId}`}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t("quality.checklist.type.hse.open_document")}
            </Link>
          </div>
        </div>
      );
    }

    if (banners.length === 0) {
      return null;
    }

    return <div className="space-y-3">{banners}</div>;
  };

  const hseAlertBanner = renderHseAlertBanners();

  let pageContent;
  if (isLoading) {
    pageContent = <ResourceLoading size="base" />;
  } else if (error) {
    pageContent = <ResourceError errors={errors} />;
  } else if (!checklist_type?.id) {
    pageContent = <ResourceEmpty />;
  } else {
    pageContent = (
      <>
        <Formik
          enableReinitialize
          initialValues={{
            id: checklist_type?.id,
            title: checklist_type?.title,
            description: checklist_type?.description,
            active: checklist_type?.active || true,
            default_response_actions: normalizeActionKeys(
              checklist_type?.default_response_actions
            ),
            items: checklist_type?.items || [],
            submit: null,
          }}
          validationSchema={Yup.object({
            title: Yup.string().max(255).required(t("title.required")),
            description: Yup.string().max(500),
          })}
          onSubmit={(values, helpers) => {
            try {
              dispatchWithToast(createOrUpdateDispatch(values), async (payload) => {
                setItemState((prevState) => ({
                  ...prevState,
                  isLoading: false,
                  checklist_type: payload?.data,
                }));
                if (id) {
                  await reloadChecklistType();
                }
              });

              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
            } catch (err) {
              toast.error("Something went wrong...");
              console.error(err);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            formDirtyRef.current = formik.dirty;
            return (
            <Form>
              <div className="space-y-6 rounded-xl border border-gray-100 bg-white px-4 py-6 shadow-sm sm:px-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-6">
                  <div className="lg:col-span-4">
                    <PMField
                      name="title"
                      type="text"
                      label={t("quality.checklist.type.titleLabel")}
                      {...formik}
                    />
                  </div>

                  <div className="flex items-start lg:col-span-2 lg:justify-end lg:pt-7">
                    <PMSwitch
                      name="active"
                      label={t("quality.checklist.type.activeLabel")}
                      {...formik}
                    />
                  </div>
                </div>

                <PMField
                  name="description"
                  type="text"
                  label={t("quality.checklist.type.introLabel")}
                  multiline
                  rows={2}
                  {...formik}
                />

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 sm:px-5">
                  <div className="text-sm font-semibold text-slate-900">
                    {t("quality.checklist.type.hse.title")}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    {t("quality.checklist.type.hse.description")}
                  </p>

                  <div className="mt-4">
                    <DisciplineCategoryChapterSelector
                      disciplineId={hseDisciplineId}
                      categoryId={hseCategoryId}
                      chapterNumber={hseChapterNumber}
                      onDisciplineChange={setHseDisciplineId}
                      onCategoryChange={(value) => {
                        setHseCategoryId(value);
                        setHseChapterNumber("");
                      }}
                      onChapterChange={setHseChapterNumber}
                      required
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={isRegisteringHse || !hseCategoryId}
                      onClick={registerInHms}
                      className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {isRegisteringHse
                        ? t("Loading")
                        : checklist_type?.hse_document?.id
                          ? t("quality.checklist.type.hse.update_placement")
                          : t("quality.checklist.type.hse.register")}
                    </button>
                    {checklist_type?.hse_document?.id ? (
                      <Link
                        to={`/dashboard/hse-ks/documents/${checklist_type.hse_document.id}`}
                        className="inline-flex text-sm font-semibold text-blue-700 hover:text-blue-900"
                      >
                        {t("quality.checklist.type.hse.open_document")} →
                      </Link>
                    ) : null}
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <p className="text-sm font-semibold text-gray-900">
                    {t("quality.checklist.type.defaultActions")}
                  </p>
                  <p className="mb-3 mt-1 text-xs text-gray-500">
                    {t("quality.checklist.type.defaultActionsHint")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CHECKLIST_RESPONSE_ACTION_KEYS.map((key) => {
                      const keys = normalizeActionKeys(
                        formik.values.default_response_actions
                      );
                      const active = keys.includes(key);
                      return (
                        <button
                          type="button"
                          key={key}
                          onClick={() =>
                            formik.setFieldValue(
                              "default_response_actions",
                              toggleKeyInList(
                                formik.values.default_response_actions,
                                key
                              )
                            )
                          }
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            active
                              ? "border-teal-600 bg-teal-600 text-white shadow-sm"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          {t(`quality.checklist.actions.${key}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-gray-100 bg-white px-4 py-6 shadow-sm sm:px-6">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 mb-1">
                      {t("quality.checklist.type.pointsHeading")}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {t("quality.checklist.type.pointsSubheading")}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setJsonDialogOpen(true)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                    >
                      <CodeBracketIcon className="h-4 w-4" />
                      {t("quality.checklist.type.json.open_dialog")}
                    </button>
                    <AIGenerator
                      module="checklist_type"
                      context={{
                        type_title: formik.values.title || "",
                        type_description: formik.values.description || "",
                      }}
                      fieldsToGenerate={["type_json"]}
                      onGenerate={(generatedFields, meta) => {
                        const raw = generatedFields?.type_json;
                        if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
                          toast.error(t("quality.checklist.type.json.ai_parse_error"));
                          return;
                        }
                        try {
                          const parsed = parseImportedChecklistTypeJson(raw);
                          const applyMode = meta?.generationOptions?.apply_mode || "replace";
                          formik.setFieldValue(
                            "items",
                            mergeImportedItemsWithExistingIds(
                              formik.values.items,
                              parsed.items,
                              applyMode
                            )
                          );
                          if (parsed.title) formik.setFieldValue("title", parsed.title);
                          if (parsed.description) {
                            formik.setFieldValue("description", parsed.description);
                          }
                          if (parsed.default_response_actions) {
                            formik.setFieldValue(
                              "default_response_actions",
                              parsed.default_response_actions
                            );
                          }
                          toast.success(
                            t("quality.checklist.type.json.ai_apply_success", {
                              count: parsed.items.length,
                            })
                          );
                        } catch (err) {
                          const code = err?.code || "unknown";
                          const key = `quality.checklist.type.json.error_${code}`;
                          const msg = t(key);
                          toast.error(
                            msg !== key ? msg : t("quality.checklist.type.json.ai_parse_error")
                          );
                        }
                      }}
                      config={aiGeneratorConfigs.checklist_type}
                      buttonLabel={t("quality.checklist.type.json.generate_with_ai")}
                      className="!rounded-full !border-slate-300 !px-4 !py-2 !text-sm !font-semibold"
                    />
                  </div>
                </div>
                <ChecklistItemLines
                  editable={true}
                  drag={true}
                  disabled={false}
                  {...formik}
                />
              </div>

              <FormikSaveBar {...formik} />

              <ChecklistTypeJsonDialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                title={formik.values.title}
                description={formik.values.description}
                default_response_actions={formik.values.default_response_actions}
                items={formik.values.items}
                onApply={({
                  items: nextItems,
                  title,
                  description,
                  default_response_actions,
                  mode,
                }) => {
                  formik.setFieldValue(
                    "items",
                    mergeImportedItemsWithExistingIds(
                      formik.values.items,
                      nextItems,
                      mode
                    )
                  );
                  if (title) formik.setFieldValue("title", title);
                  if (description != null && description !== "") {
                    formik.setFieldValue("description", description);
                  }
                  if (default_response_actions) {
                    formik.setFieldValue("default_response_actions", default_response_actions);
                  }
                }}
              />
            </Form>
            );
          }}
        </Formik>
      </>
    );
  }

  return (
    <DashboardHeader
      subtitle=""
      actions={headerActions}
      title={checklist_type?.displayTitle || "Mangler title"}
      meta={headerMeta}
      backButton={
        returnTo
          ? {
              path: returnTo,
              icon: ArrowLeftIcon,
              label: t("quality.checklist.type.hse.back_to_document"),
              replace: true,
            }
          : undefined
      }
      mainButton={{
        label: "Edit",
        path: null,
        color: "",
        icon: PencilIcon,
      }}
    >
      <div className="mx-auto w-full max-w-7xl space-y-8 px-4 pb-10 pt-8 sm:px-6 lg:px-8">
        {hseAlertBanner}
        {pageContent}
      </div>
    </DashboardHeader>
  );
};
